import { Form, Head, usePage } from "@inertiajs/react"

import InputError from "@/components/input-error"
import TextLink from "@/components/text-link"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Spinner } from "@/components/ui/spinner"
import AuthLayout from "@/layouts/auth-layout"
import { signInPath, signUpPath } from "@/routes"

export default function Register() {
  const { props } = usePage()
  const isMobileApp = (props.app as { mobile_app?: boolean })?.mobile_app

  const handleGoogleLogin = async () => {
    if (isMobileApp) {
      const { handleNativeGoogleLogin } = await import("@/lib/native-auth")
      await handleNativeGoogleLogin()
    } else {
      window.location.assign("/auth/google_oauth2")
    }
  }

  return (
    <AuthLayout
      title="Crear cuenta"
      description="Ingresá tus datos para registrarte"
    >
      <Head title="Registrarse" />

      <div className="flex flex-col gap-6">
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handleGoogleLogin}
        >
          <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Registrarse con Google
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <Separator className="w-full" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background text-muted-foreground px-2">o</span>
          </div>
        </div>

        <Form
          method="post"
          action={signUpPath()}
          resetOnSuccess={["password", "password_confirmation"]}
          disableWhileProcessing
          className="flex flex-col gap-6"
        >
          {({ processing, errors }) => (
            <>
              <div className="grid gap-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="first_name">Nombre</Label>
                    <Input
                      id="first_name"
                      type="text"
                      name="first_name"
                      required
                      autoFocus
                      tabIndex={1}
                      autoComplete="given-name"
                      disabled={processing}
                      placeholder="Nombre"
                    />
                    <InputError messages={errors.first_name} className="mt-2" />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="last_name">Apellido</Label>
                    <Input
                      id="last_name"
                      type="text"
                      name="last_name"
                      required
                      tabIndex={2}
                      autoComplete="family-name"
                      disabled={processing}
                      placeholder="Apellido"
                    />
                    <InputError messages={errors.last_name} className="mt-2" />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    required
                    tabIndex={3}
                    autoComplete="email"
                    placeholder="email@ejemplo.com"
                  />
                  <InputError messages={errors.email} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="password">Contraseña</Label>
                  <Input
                    id="password"
                    type="password"
                    name="password"
                    required
                    tabIndex={4}
                    autoComplete="new-password"
                    placeholder="Contraseña"
                  />
                  <InputError messages={errors.password} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="password_confirmation">
                    Confirmar contraseña
                  </Label>
                  <Input
                    id="password_confirmation"
                    type="password"
                    name="password_confirmation"
                    required
                    tabIndex={5}
                    autoComplete="new-password"
                    placeholder="Confirmar contraseña"
                  />
                  <InputError messages={errors.password_confirmation} />
                </div>

                <Button
                  type="submit"
                  variant="cta"
                  className="mt-2 w-full"
                  tabIndex={6}
                >
                  {processing && <Spinner />}
                  Crear cuenta
                </Button>
              </div>

              <div className="text-muted-foreground text-center text-sm">
                ¿Ya tenés cuenta?{" "}
                <TextLink href={signInPath()} tabIndex={6}>
                  Iniciar sesión
                </TextLink>
              </div>
            </>
          )}
        </Form>
      </div>
    </AuthLayout>
  )
}
